import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { VehiculoService } from '@app/core/services/vehiculo.service';
import { getYears } from '@app/core/tools/get-years';
import { Distrito } from '@app/models/distrito';
import { Empresa } from '@app/models/empresa';
import { Sector } from '@app/models/sector';
import { TipoVehiculo } from '@app/models/tipo-vehiculo';
import { Vehiculo } from '@app/models/vehiculo';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { FlotaOpciones } from './opciones';

@Component({
    selector: 'app-nueva-flota',
    standalone: false,
    templateUrl: './nueva-flota.component.html',
    styleUrl: './nueva-flota.component.scss'
})
export class NuevaFlotaComponent {
    private readonly maxIntValue = 2_147_483_647;
    private readonly ref = inject(DynamicDialogRef);
    private readonly service = inject(VehiculoService);
    private readonly messageService = inject(MessageService);

    opcionesSelect: FlotaOpciones = {
        titularidades: [
            { label: '--Seleccione--', value: '' },
            { label: 'Alquilado', value: 'Alquilado' },
            { label: 'Propio', value: 'Propio' }
        ],
        tipos: [],
        marcas: [],
        sectores: [],
        distritos: [],
        empresas: [],
        anios: [{ label: '--Seleccione--', value: '' }],
        combustibles: [
            { label: '--Seleccione--', value: '' },
            { label: 'Nafta', value: 'Nafta' },
            { label: 'Gasoil', value: 'Gasoil' }
        ],
        tacografos: [
            { label: 'RSV', value: 'RSV' },
            { label: 'MicroTrack', value: 'MicroTrack' }
        ]
    };

    form = new FormGroup({
        titularidad: new FormControl<string | null>(null, { nonNullable: true, validators: [Validators.required] }), // Da si es propio o no
        empresa: new FormControl<Empresa | null>(null, { nonNullable: true, validators: [Validators.required] }),
        interno: new FormControl<string>('', { nonNullable: true, validators: [Validators.required, Validators.max(this.maxIntValue)] }),
        dominio: new FormControl<string>(''),
        motor: new FormControl<string>(''),
        chasis: new FormControl<string>(''),
        tipo_vehiculo: new FormControl<TipoVehiculo | null>(null, { nonNullable: true, validators: [Validators.required] }),
        identificador_tacografo: new FormControl<string>(''),
        marca: new FormControl<{ id: string; nombre: string } | null>(null, { nonNullable: true, validators: [Validators.required] }),
        sector: new FormControl<Sector | null>(null, { nonNullable: true, validators: [Validators.required] }),
        distrito: new FormControl<Distrito | null>(null, { nonNullable: true, validators: [Validators.required] }),
        modelo: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
        anio: new FormControl<string | null>(null, { nonNullable: true, validators: [Validators.required] }),
        tipo_combustible: new FormControl<string | null>(null), // Da el string para combustible
        tacografo: new FormControl<string | null>(null), // Da el string para marca_tacografo
        asientos: new FormControl<string>(''),
        observaciones: new FormControl<string>('')
    });

    isPropio = false;
    async ngOnInit() {
        const { sectores, distritos, empresas, tipos, marcas } = await firstValueFrom(this.service.initForm());
        this.opcionesSelect.distritos = distritos;
        this.opcionesSelect.sectores = sectores;
        this.opcionesSelect.empresas = empresas;
        this.opcionesSelect.tipos = tipos;
        this.opcionesSelect.marcas = marcas;

        const years = getYears(2000).map((year) => ({ label: year.toString(), value: year.toString() }));

        this.opcionesSelect.anios = [...this.opcionesSelect.anios, ...years];

        // Escucha cambios en el select de titularidad
        this.form.get('titularidad')?.valueChanges.subscribe((titularidad: string | null) => {
            this.isPropio = false;
            // resetear cliente si cambia la titularidad
            this.form.get('empresa')?.reset();
            if (titularidad == 'Propio') {
                this.isPropio = true;
                const blister = empresas.find((empresa) => empresa.nombre == 'Blister') || null;
                this.form.get('empresa')?.patchValue(blister);
            }
        });
    }

    async confirmar() {
        if (!this.form.valid) {
            this.form.markAllAsTouched();
            return;
        }

        const fv = this.form.controls;

        // Preparar payload acorde al DTO del backend (manejo flexible si los selects devuelven objetos o primitives)
        const payload: any = {
            titularidad: fv.titularidad.value,
            dominio: fv.dominio.value,
            marca: fv.marca.value?.nombre ?? (fv.marca.value as any)?.marca ?? null,
            modelo: fv.modelo.value,
            identificador_tacografo: fv.identificador_tacografo.value,
            propio: fv.titularidad.value === 'Propio' ? 1 : 0,
            marca_tacografo: fv.tacografo.value,
            observaciones: fv.observaciones.value,
            combustible: fv.tipo_combustible.value,
            nro_interno: fv.interno.value ? +fv.interno.value : undefined,
            nro_motor: fv.motor.value ? +fv.motor.value : undefined,
            nro_chasis: fv.chasis.value ? +fv.chasis.value : undefined,
            anio: fv.anio.value ? +fv.anio.value : undefined,
            capacidad: fv.asientos.value ? +fv.asientos.value : undefined,
            distrito: fv.distrito.value?.id ? ({ id: fv.distrito.value.id } as any) : undefined,
            sector: fv.sector.value?.id ? ({ id: fv.sector.value.id } as any) : undefined,
            empresa: fv.empresa.value?.id ? ({ id: fv.empresa.value.id } as any) : undefined,
            tipo_vehiculo: fv.tipo_vehiculo.value?.id ? ({ id: fv.tipo_vehiculo.value.id } as any) : undefined,
            tipoVehiculoId: fv.tipo_vehiculo.value?.id
        };

        try {
            const res = await firstValueFrom(this.service.create(payload));

            this.ref.close(res);
        } catch (err) {
            console.error('Error creando vehículo', err);
            const msg = this.resolveVehiculoErrorMessage(err);
            this.messageService.add({ severity: 'error', summary: 'Error', detail: msg });
        }
    }

    private resolveVehiculoErrorMessage(err: unknown): string {
        const body = (err as any)?.error ?? err;
        const sql = body?.sqlMessage ?? body?.driverError?.sqlMessage;
        const text = `${body?.msg ?? body?.message ?? sql ?? ''}`.toLowerCase();

        if (text.includes('out of range') && text.includes('nro_interno')) {
            return `El Número de Interno es demasiado grande. Debe ser menor o igual a ${this.maxIntValue}.`;
        }

        return (err as any)?.error?.msg || (err as any)?.message || 'No se pudo crear la unidad.';
    }

    cancelar() {
        this.ref.close();
    }
}
